import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import getClient from '@refly-packages/ai-workspace-common/requests/proxiedRequest';
import { genCanvasID } from '@refly/utils';
import { useHandleSiderData } from '@refly-packages/ai-workspace-common/hooks/use-handle-sider-data';
import { useWorkflowExecutionPolling } from './use-workflow-execution-polling';
import { useCanvasStoreShallow } from '@refly/stores';
import {
  GetWorkflowDetailResponse,
  InitializeWorkflowRequest,
  WorkflowExecution,
} from '@refly/openapi-schema';
import { useVariablesManagement } from '@refly-packages/ai-workspace-common/hooks/use-variables-management';
import { guessModelProviderError, ModelUsageQuotaExceeded } from '@refly/errors';
import { useCanvasNodeExecutions } from './canvas/use-canvas-node-executions';

export const useInitializeWorkflow = (
  canvasId: string,
  forceSyncState: ({ syncRemote }: { syncRemote?: boolean }) => Promise<void>,
) => {
  const { t } = useTranslation();
  useCanvasNodeExecutions(canvasId);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [newModeLoading, setNewModeLoading] = useState(false);
  const [latestWorkflowDetail, setLatestWorkflowDetail] = useState<WorkflowExecution>();
  const { getCanvasList } = useHandleSiderData();
  // [Voucher feature hidden] showEarnedVoucherPopup, setHasFirstExecutionToday, planType removed

  const { executionId, setCanvasExecutionId } = useCanvasStoreShallow(
    (state) => ({
      executionId: state.canvasExecutionId[canvasId],
      setCanvasExecutionId: state.setCanvasExecutionId,
    }),
  );
  const { data: workflowVariables } = useVariablesManagement(canvasId);

  // Memoize callbacks to avoid recreating them on every render
  const handleComplete = useMemo(
    () => async (status: string, data: GetWorkflowDetailResponse) => {
      if (status === 'finish') {
        message.success(
          t('canvas.workflow.run.completed') || 'Workflow execution completed successfully',
        );
      } else if (status === 'failed') {
        // Check if this is a credit insufficient error
        const nodeExecutions = data?.data?.nodeExecutions || [];
        const hasCreditInsufficientError = nodeExecutions.some((nodeExecution: any) => {
          if (nodeExecution.errorMessage) {
            const error = guessModelProviderError(nodeExecution.errorMessage);
            return error instanceof ModelUsageQuotaExceeded;
          }
          return false;
        });

        // Only show error notification if NOT aborted by user and NOT credit insufficient
        if (!data?.data?.abortedByUser && !hasCreditInsufficientError) {
          message.error(t('canvas.workflow.run.failed') || 'Workflow execution failed');
        }
      }
    },
    [t, canvasId],
  );

  const handleError = useMemo(
    () => (_error: any) => {
      message.error(t('canvas.workflow.run.error') || 'Error monitoring workflow execution');
    },
    [t],
  );

  // Use the polling hook for workflow execution monitoring
  const {
    status: workflowStatus,
    data: workflowDetail,
    error: pollingError,
    isPolling: isCurrentlyPolling,
    startPolling,
    stopPolling,
  } = useWorkflowExecutionPolling({
    executionId,
    canvasId: canvasId || '',
    enabled: !!executionId || !!canvasId,
    interval: 2000,
    onComplete: handleComplete,
    onError: handleError,
  });

  useEffect(() => {
    if (!canvasId || executionId) {
      return;
    }

    let cancelled = false;
    const loadLatestWorkflowDetail = async () => {
      const { data: listResponse, error: listError } = await getClient().listWorkflowExecutions({
        query: { canvasId, page: 1, pageSize: 1, order: 'creationDesc' },
      });
      const latestExecutionId = listResponse?.data?.[0]?.executionId;
      if (cancelled || listError || !latestExecutionId) {
        if (!cancelled) setLatestWorkflowDetail(undefined);
        return;
      }

      const { data: detailResponse, error: detailError } = await getClient().getWorkflowDetail({
        query: { executionId: latestExecutionId },
      });
      if (!cancelled) {
        const latestDetail = detailError ? undefined : detailResponse?.data;
        setLatestWorkflowDetail(latestDetail);
      }
    };

    void loadLatestWorkflowDetail();
    return () => {
      cancelled = true;
    };
  }, [canvasId, executionId]);

  const initializeWorkflow = useCallback(
    async (param: InitializeWorkflowRequest) => {
      try {
        setLoading(true);
        await forceSyncState({ syncRemote: true });

        // [Voucher feature hidden] Voucher popup trigger logic removed

        const { data, error } = await getClient().initializeWorkflow({
          body: {
            variables: workflowVariables,
            ...param,
          },
        });

        if (error) {
          console.error('Failed to initialize workflow:', error);
          message.error(t('common.operationFailed') || 'Operation failed');
          return false;
        }
        if (data?.data?.workflowExecutionId && canvasId) {
          setLatestWorkflowDetail(undefined);
          setCanvasExecutionId(canvasId, data.data.workflowExecutionId);
        }

        // [Voucher feature hidden] Voucher polling and popup display removed

        message.success({
          content: t('canvas.workflow.run.startRunning') || 'Your workflow starts running',
          duration: 5,
        });
        return true;
      } catch (err) {
        console.error('Error initializing workflow:', err);
        message.error(t('common.operationFailed') || 'Operation failed');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [t, canvasId, setCanvasExecutionId, forceSyncState, workflowVariables],
  );

  const initializeWorkflowInNewCanvas = useCallback(
    async (canvasId: string) => {
      try {
        setNewModeLoading(true);
        await forceSyncState({ syncRemote: true });

        const newCanvasId = genCanvasID();

        const { error } = await getClient().initializeWorkflow({
          body: {
            canvasId: newCanvasId,
            sourceCanvasId: canvasId,
          },
        });

        if (error) {
          console.error('Failed to initialize workflow in new canvas:', error);
          message.error(t('common.operationFailed') || 'Operation failed');
          return false;
        }

        message.success(
          t('common.putSuccess') || 'Workflow initialized in new canvas successfully',
        );

        // Refresh sidebar canvas list to include the new canvas
        await getCanvasList();

        // Wait for 2 seconds before navigating to the new canvas
        await new Promise((resolve) => setTimeout(resolve, 125));
        navigate(`/workflow/${newCanvasId}`);
        return true;
      } catch (err) {
        console.error('Error initializing workflow in new canvas:', err);
        message.error(t('common.operationFailed') || 'Operation failed');
        return false;
      } finally {
        setNewModeLoading(false);
      }
    },
    [t, navigate, getCanvasList, forceSyncState],
  );

  return {
    initializeWorkflow,
    initializeWorkflowInNewCanvas,
    isInitializing: loading,
    newModeLoading,
    // Workflow execution polling state
    executionId,
    workflowStatus,
    workflowDetail: workflowDetail ?? latestWorkflowDetail,
    isPolling: isCurrentlyPolling,
    pollingError,
    startPolling,
    stopPolling,
  };
};
